import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { useAuth } from '../contexts/AuthContext';
import { useRegistrationStatus } from './useRegistrationStatus';
import type { TeamInvitationData, TeamMemberData } from '../types/n8nResponses';
import type { CampaignMetadata } from '../types';

export const useCampaigns = (userEmailOverride?: string) => {
  const { currentUser, targetEmail } = useAuth();
  const { isRegistered } = useRegistrationStatus();
  const userEmail = userEmailOverride || targetEmail || currentUser?.email;
  const isImpersonating = Boolean(targetEmail && targetEmail !== currentUser?.email);

  const query = useQuery({
    queryKey: queryKeys.campaigns.list(userEmail),
    queryFn: () => appwriteQueryFns.campaigns.all(userEmail),
    // Allow impersonation to fetch target-user data even if the local registration gate is false.
    enabled: !!userEmail && (isRegistered || isImpersonating),
    // campaign_metrics is a single lightweight n8n call (the heavy leads fetch
    // is lazy per-campaign), and campaign data DOES change underneath us:
    // team-view assignments and ClickUp edits reach us through a DB that syncs
    // every ~5 min. The old 15-min staleTime made assignments invisible for
    // 15+ minutes — and the persisted-cache hydration resets dataUpdatedAt, so
    // even a refresh served stale data as fresh. 2 min + mount/focus refetch +
    // a 5-min poll (aligned with the sync cadence) keeps the list truthful.
    staleTime: 2 * 60 * 1000,   // 2 minutes
    gcTime: 30 * 60 * 1000,     // 30 minutes (keep in memory longer)
    refetchOnWindowFocus: true, // catch up after tab switches (only when stale)
    // Always refresh on impersonated mounts so we never show the wrong team's cached roster.
    refetchOnMount: isImpersonating ? 'always' : true,
    // Poll in step with the upstream DB sync so team-view assignments surface
    // without a manual reload. One cheap campaign_metrics call per 5 min;
    // a background tab pauses it (refetchIntervalInBackground defaults false).
    refetchInterval: 5 * 60 * 1000,
    // Keep previous data visible while re-fetching after reconnect
    placeholderData: (previousData: any) => previousData,
  });

  // Expose campaigns as `data` (backwards compat) and teamMembers as a separate field.
  // teamMembers is non-empty only when the caller has team_admin/super_admin label
  // and the n8n-proxy has injected them into the campaign_metrics response.
  //
  // Derive with useMemo keyed on query.data so the returned arrays/refs stay stable
  // between renders when the underlying data hasn't changed. Without this, `?? []`
  // produces a fresh array every render, invalidating consumer memos/children and
  // amplifying re-render churn.
  const rawData = query.data;
  const derived = useMemo(
    () => ({
      data: rawData?.campaigns as CampaignMetadata[] | undefined,
      teamMembers: (rawData?.teamMembers ?? []) as TeamMemberData[],
      teamInvitations: (rawData?.teamInvitations ?? []) as TeamInvitationData[],
    }),
    [rawData]
  );

  return {
    ...query,
    ...derived,
  };
};
